import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  RealtimeKitProvider,
  useRealtimeKitClient,
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import {
  RtkMeeting,
  createDefaultConfig,
  type UIConfig,
  useLanguage,
} from "@cloudflare/realtimekit-react-ui";
import { api } from "../api";

/**
 * End-of-session copy (replaces preset strings like “You left the meeting.”).
 * Preset themes can ship their own phrases; we override the usual keys.
 */
const RECORD_MEETING_I18N = useLanguage({
  ended: "Saving recording…",
  "ended.left": "Saving recording…",
  "ended.kicked": "Saving recording…",
  "ended.disconnected": "Saving recording…",
  "recording.stopping": "Saving recording…",
  "recording.stopped": "Recording saved.",
});

function uiChildTag(item: unknown): string | null {
  if (typeof item === "string") return item;
  if (Array.isArray(item) && typeof item[0] === "string") return item[0];
  return null;
}

const STRIP_UI_TAGS = new Set(["rtk-leave-button", "rtk-recording-indicator"]);

/** Controls we remove from the tree so preset/theme merges cannot resurrect them in slots. */
const STRIP_EXTRA_CONTROL_TAGS = new Set([
  "rtk-chat-toggle",
  "rtk-polls-toggle",
  "rtk-participants-toggle",
  "rtk-plugins-toggle",
  "rtk-ai-toggle",
  "rtk-screen-share-toggle",
  "rtk-livestream-toggle",
  "rtk-webinar-stage-toggle",
  "rtk-stage-toggle",
  "rtk-more-toggle",
]);

const HIDE_CONTROL_STYLE: Record<string, string> = {
  display: "none",
  visibility: "hidden",
  pointerEvents: "none",
  width: "0",
  height: "0",
  margin: "0",
  padding: "0",
  overflow: "hidden",
  opacity: "0",
};

function hiddenControlStyles(tags: readonly string[]): UIConfig["styles"] {
  return Object.fromEntries(tags.map((tag) => [tag, { ...HIDE_CONTROL_STYLE }]));
}

/**
 * Remove Leave + stock recording indicator everywhere under `root` (arrays, `children`,
 * `add` tuples, nested objects). Top-level-only filtering misses control-bar slots.
 */
function deepStripLeaveAndStockRecording(cfg: UIConfig): UIConfig {
  const stripTags = new Set([...STRIP_UI_TAGS, ...STRIP_EXTRA_CONTROL_TAGS]);

  function walk(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value
        .map(walk)
        .filter((item) => {
          const tag = uiChildTag(item);
          if (tag && stripTags.has(tag)) return false;
          if (Array.isArray(item) && typeof item[0] === "string") {
            if (stripTags.has(item[0])) return false;
          }
          return true;
        });
    }
    if (value !== null && typeof value === "object") {
      const o = value as Record<string, unknown>;
      const next: Record<string, unknown> = {};
      for (const k of Object.keys(o)) {
        next[k] = walk(o[k]);
      }
      return next;
    }
    return value;
  }

  const extraHidden = hiddenControlStyles([
    ...STRIP_EXTRA_CONTROL_TAGS,
    "rtk-leave-button",
  ]);

  return {
    ...cfg,
    root: walk(cfg.root) as UIConfig["root"],
    styles: {
      ...cfg.styles,
      ...extraHidden,
      /* Belt-and-suspenders: preset merge can re-insert leave before our config wins. */
      "rtk-leave-button": { ...HIDE_CONTROL_STYLE },
    },
  };
}

/**
 * Solo journal: only settings (left), mic + camera (center), nothing on the right.
 * Mobile row matches — settings is included (default theme hides it behind “more”).
 */
function applyMinimalRecordControlbar(cfg: UIConfig): UIConfig {
  const root = { ...(cfg.root as Record<string, unknown>) };
  root["div#controlbar-left"] = [];
  root["div#controlbar-center"] = [
    "rtk-mic-toggle",
    "rtk-camera-toggle",
    "rtk-settings-toggle",
  ];
  root["div#controlbar-right"] = [];
  root["div#controlbar-mobile"] = [
    "rtk-mic-toggle",
    "rtk-camera-toggle",
    "rtk-settings-toggle",
  ];
  root["rtk-more-toggle.activeMoreMenu"] = [];
  root["rtk-more-toggle.activeMoreMenu.sm"] = [];
  root["rtk-more-toggle.activeMoreMenu.md"] = [];
  return { ...cfg, root: root as UIConfig["root"] };
}

/** Fresh config for each apply — RealtimeKit mutates `config` in place when merging preset. */
function buildRecordMeetingConfig(): UIConfig {
  return applyMinimalRecordControlbar(deepStripLeaveAndStockRecording(createDefaultConfig()));
}

const RECORD_MEETING_CONFIG: UIConfig = buildRecordMeetingConfig();

type RtkMeetingElement = HTMLElement & {
  loadConfigFromPreset: boolean;
  config: UIConfig;
  t: (key: string) => string;
};

/** Cloud encoder often needs a few seconds after join before REC is “real”; we show an honest countdown. */
const RECORDING_WARMUP_SECONDS = 10;

type ImportResponse =
  | { ok: true; status: string; alreadyImported?: boolean }
  | { pending: true; reason?: string };

const IMPORT_POLL_MS = 3000;
const IMPORT_MAX_ATTEMPTS = 45;
/** Consecutive no_recordings_yet before giving up (RealtimeKit can be slow to index). */
const NO_RECORDING_GIVE_UP = 24;

async function deleteDraftRecording(recordingId: string): Promise<void> {
  await api(`/api/recordings/${encodeURIComponent(recordingId)}/draft`, {
    method: "DELETE",
  });
}

async function pollImportFromRealtime(recordingId: string): Promise<
  | { kind: "saved"; message: string }
  | { kind: "nothing" }
  | { kind: "error"; message: string }
> {
  let noRecordingStreak = 0;

  for (let attempt = 0; attempt < IMPORT_MAX_ATTEMPTS; attempt++) {
    const r = await api<ImportResponse>(
      `/api/recordings/${encodeURIComponent(recordingId)}/import-from-realtime`,
      { method: "POST", body: "{}" },
    );

    if (!r.ok) {
      return {
        kind: "error",
        message:
          r.error ||
          "Could not save the recording. You can try again in a moment.",
      };
    }

    const d = r.data;
    if (d && "ok" in d && d.ok) {
      if (d.status === "processing_transcribe" || d.alreadyImported) {
        return {
          kind: "saved",
          message: d.alreadyImported
            ? "Already saved — transcription may still be running. Check Library."
            : "Saved — transcription has started. Check Library in a minute.",
        };
      }
    }

    if (d && "pending" in d && d.pending) {
      const reason = (d as { reason?: string }).reason;
      if (reason === "no_recordings_yet") {
        noRecordingStreak++;
        if (noRecordingStreak >= NO_RECORDING_GIVE_UP) {
          return { kind: "nothing" };
        }
      } else {
        noRecordingStreak = 0;
      }
      await new Promise((res) => setTimeout(res, IMPORT_POLL_MS));
      continue;
    }

    return { kind: "error", message: "Unexpected response while saving." };
  }

  return {
    kind: "error",
    message:
      "Recording is still processing on RealtimeKit’s side. Wait a bit, then tap End session again.",
  };
}

/** Same storage shape as `@cloudflare/realtimekit-ui` `getUserPreferences` / video settings toggle. */
const RTK_PREFS_KEY = "rtk-prefs";

function readRtkMirrorVideo(): boolean {
  try {
    const raw = localStorage.getItem(RTK_PREFS_KEY);
    if (!raw) return true;
    const prefs = JSON.parse(raw) as Record<string, string>;
    const v = prefs["mirror-video"];
    return v != null && v !== "" ? v === "true" : true;
  } catch {
    return true;
  }
}

/**
 * RTK writes `mirror-video` into `rtk-prefs` when the user toggles Settings → mirror.
 * Same-tab updates don't fire `storage`, so we poll lightly while the preview is active.
 */
function useRtkMirrorVideoPreference(active: boolean): boolean {
  const [mirror, setMirror] = useState(readRtkMirrorVideo);

  useEffect(() => {
    if (!active) return;
    setMirror(readRtkMirrorVideo());
    const onStorage = (e: StorageEvent) => {
      if (e.key === RTK_PREFS_KEY || e.key === null) setMirror(readRtkMirrorVideo());
    };
    window.addEventListener("storage", onStorage);
    const id = window.setInterval(() => {
      const next = readRtkMirrorVideo();
      setMirror((prev) => (prev === next ? prev : next));
    }, 400);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(id);
    };
  }, [active]);

  return mirror;
}

/**
 * RTK's main grid hides the local tile when you're treated as recorder / off-stage
 * (common once cloud recording runs). Self preview uses the same mirror preference as RTK tiles
 * (`scaleX(-1)` when mirror is on; default on).
 */
function LocalSelfPreview() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const roomJoined = useRealtimeKitSelector((m) => m.self.roomJoined);
  const videoEnabled = useRealtimeKitSelector((m) => m.self.videoEnabled);
  const videoTrack = useRealtimeKitSelector((m) => m.self.videoTrack);
  const mirrorVideo = useRtkMirrorVideoPreference(roomJoined);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (roomJoined && videoEnabled && videoTrack) {
      const stream = new MediaStream();
      stream.addTrack(videoTrack);
      el.srcObject = stream;
    } else {
      el.srcObject = null;
    }
  }, [roomJoined, videoEnabled, videoTrack]);

  if (!roomJoined) return null;

  return (
    <div className="local-self-preview">
      <div className="local-self-preview__label">You</div>
      <video
        ref={videoRef}
        className={`local-self-preview__video${mirrorVideo ? " local-self-preview__video--mirror" : ""}`}
        autoPlay
        playsInline
        muted
      />
      {!videoEnabled ? (
        <p className="local-self-preview__off muted">Camera is off — use the toolbar to turn it on.</p>
      ) : null}
    </div>
  );
}

function RecordingStatusHud() {
  const roomJoined = useRealtimeKitSelector((m) => m.self.roomJoined);
  const [phase, setPhase] = useState<"off" | "warmup" | "live">("off");
  const [countdown, setCountdown] = useState(RECORDING_WARMUP_SECONDS);

  useEffect(() => {
    if (!roomJoined) {
      setPhase("off");
      return;
    }
    setPhase("warmup");
    setCountdown(RECORDING_WARMUP_SECONDS);
    const t0 = Date.now();
    const id = window.setInterval(() => {
      const elapsed = (Date.now() - t0) / 1000;
      const left = Math.max(0, Math.ceil(RECORDING_WARMUP_SECONDS - elapsed));
      setCountdown(left);
      if (left <= 0) {
        setPhase("live");
        window.clearInterval(id);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [roomJoined]);

  if (!roomJoined || phase === "off") return null;

  const progressPct =
    phase === "warmup"
      ? ((RECORDING_WARMUP_SECONDS - countdown) / RECORDING_WARMUP_SECONDS) * 100
      : 100;

  return (
    <div className="recording-hud-root">
      {phase === "warmup" ? (
        <div
          className="recording-warmup-overlay"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="recording-warmup-card">
            <p className="recording-warmup-label">Cloud recording</p>
            <div className="recording-warmup-count" aria-hidden>
              {countdown}
            </div>
            <p className="recording-warmup-hint">
              Encoder and cloud recorder are starting. You can use mic and camera
              below — this overlay is informational only.
            </p>
            <div className="recording-warmup-track">
              <div
                className="recording-warmup-track__fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>
      ) : null}
      {phase === "live" ? (
        <div
          className="rec-indicator"
          role="status"
          aria-label="Cloud recording is active"
        >
          <span className="rec-indicator__dot" aria-hidden />
          <span className="rec-indicator__text">REC</span>
        </div>
      ) : null}
    </div>
  );
}

function MeetingInner({
  onRegisterRelease,
}: {
  onRegisterRelease: (release: () => Promise<void>) => void;
}) {
  const { meeting } = useRealtimeKitMeeting();
  const meetingRef = useRef(meeting);
  meetingRef.current = meeting;
  const rtkHostRef = useRef<RtkMeetingElement | null>(null);

  /**
   * `rtk-meeting` forces `loadConfigFromPreset = true` in `connectedCallback`, then
   * `meetingChanged` replaces `config` from the theme before React reapplies props.
   * Re-assert our stripped config, `t`, and `loadConfigFromPreset` after mount / meeting bind.
   */
  useLayoutEffect(() => {
    if (!meeting) return;
    const el = rtkHostRef.current;
    if (!el) return;
    const apply = () => {
      el.loadConfigFromPreset = false;
      el.config = buildRecordMeetingConfig();
      el.t = RECORD_MEETING_I18N;
    };
    apply();
    const id = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(id);
  }, [meeting]);

  useEffect(() => {
    const release = async () => {
      const m = meetingRef.current;
      if (!m) return;
      const self = m.self;
      try {
        if (self.screenShareEnabled) await self.disableScreenShare();
        if (self.videoEnabled) await self.disableVideo();
        if (self.audioEnabled) await self.disableAudio();
      } catch (e) {
        console.warn("Stopping local media", e);
      }
      try {
        await m.leaveRoom();
      } catch (e) {
        console.warn("leaveRoom", e);
      }
    };
    onRegisterRelease(release);
    return () => {
      onRegisterRelease(async () => {});
    };
  }, [onRegisterRelease]);

  return (
    <div className="rtk-wrap">
      <LocalSelfPreview />
      <div className="rtk-wrap-meeting-host">
        <RtkMeeting
          ref={rtkHostRef}
          mode="fill"
          meeting={meeting}
          showSetupScreen={false}
          config={RECORD_MEETING_CONFIG}
          leaveOnUnmount={false}
          loadConfigFromPreset={false}
          t={RECORD_MEETING_I18N}
        />
      </div>
      <RecordingStatusHud />
    </div>
  );
}

function RealtimeSession({
  authToken,
  onRegisterRelease,
}: {
  authToken: string;
  onRegisterRelease: (release: () => Promise<void>) => void;
}) {
  const [client, initClient] = useRealtimeKitClient();

  useEffect(() => {
    initClient({
      authToken,
      defaults: { audio: true, video: true },
    }).catch(console.error);
  }, [authToken, initClient]);

  return (
    <RealtimeKitProvider
      value={client}
      fallback={<p className="muted">Connecting to RealtimeKit…</p>}
    >
      {client ? (
        <MeetingInner onRegisterRelease={onRegisterRelease} />
      ) : (
        <p className="muted">Preparing camera and microphone…</p>
      )}
    </RealtimeKitProvider>
  );
}

export default function Record() {
  const [session, setSession] = useState<{
    authToken: string;
    recordingId: string;
  } | null>(null);
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [ending, setEnding] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [endBanner, setEndBanner] = useState<{
    tone: "error" | "info";
    text: string;
  } | null>(null);

  const releaseMediaRef = useRef<(() => Promise<void>) | null>(null);
  const sessionPopoverRef = useRef<HTMLDivElement>(null);
  const registerRelease = useCallback((fn: () => Promise<void>) => {
    releaseMediaRef.current = fn;
  }, []);

  useEffect(() => {
    if (!session) return;
    const el = sessionPopoverRef.current;
    if (!el) return;
    const id = window.requestAnimationFrame(() => {
      el.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [session]);

  const start = useCallback(async () => {
    setLoading(true);
    setHint("");
    const r = await api<{
      authToken?: string;
      recordingId?: string;
      hint?: string;
      missing?: string[];
      steps?: string[];
    }>("/api/realtime/session", {
      method: "POST",
      body: JSON.stringify({}),
    });
    setLoading(false);
    if (!r.ok || !r.data?.authToken || !r.data?.recordingId) {
      const eb = r.errorBody as
        | {
            hint?: string;
            missing?: string[];
            steps?: string[];
            kind?: string;
            detail?: string;
          }
        | undefined;
      const lines = [
        eb?.hint || r.error,
        eb?.detail ? `API: ${eb.detail}` : null,
        eb?.kind ? `(${eb.kind})` : null,
        eb?.missing?.length
          ? `Missing env: ${eb.missing.join(", ")}`
          : null,
        ...(eb?.steps ?? []),
      ].filter(Boolean) as string[];
      setHint(
        lines.length > 0
          ? lines.join("\n")
          : "Could not start RealtimeKit. Configure .dev.vars (see .dev.vars.example).",
      );
      return;
    }
    setSession({ authToken: r.data.authToken, recordingId: r.data.recordingId });
    setEndBanner(null);
  }, []);

  const endSession = useCallback(async () => {
    if (!session || canceling) return;
    setEnding(true);
    setEndBanner(null);
    await api(
      `/api/recordings/${encodeURIComponent(session.recordingId)}/stop-realtime-recording`,
      { method: "POST", body: "{}" },
    );
    await releaseMediaRef.current?.();
    const result = await pollImportFromRealtime(session.recordingId);
    setEnding(false);
    if (result.kind === "saved") {
      setSession(null);
      setEndBanner({ tone: "info", text: result.message });
      return;
    }
    if (result.kind === "nothing") {
      await deleteDraftRecording(session.recordingId);
      setSession(null);
      setEndBanner({
        tone: "info",
        text: "Session ended. No recording was found to save (you can start a new session anytime).",
      });
      return;
    }
    setEndBanner({ tone: "error", text: result.message });
  }, [session, canceling]);

  const cancelSession = useCallback(async () => {
    if (!session || ending) return;
    const { recordingId } = session;
    setCanceling(true);
    setEndBanner(null);
    await api(
      `/api/recordings/${encodeURIComponent(recordingId)}/stop-realtime-recording`,
      { method: "POST", body: "{}" },
    );
    await releaseMediaRef.current?.();
    setSession(null);
    const dr = await api(`/api/recordings/${encodeURIComponent(recordingId)}/draft`, {
      method: "DELETE",
    });
    setCanceling(false);
    if (!dr.ok) {
      setEndBanner({
        tone: "error",
        text:
          dr.error ||
          "Could not remove the draft. Check Library for a stray entry.",
      });
      return;
    }
    setEndBanner({
      tone: "info",
      text: "Session discarded — nothing was saved.",
    });
  }, [session, ending]);

  const sessionBusy = ending || canceling;

  return (
    <>
      <h1>Record</h1>
      <p className="muted">
        Camera and microphone are used only for your personal log. Grant
        permissions when the browser asks.
      </p>

      {!session ? (
        <>
          <div className="card">
            {endBanner ? (
              <p
                className={endBanner.tone === "error" ? "error" : "muted"}
                style={{ marginBottom: "0.75rem" }}
              >
                {endBanner.text}
              </p>
            ) : null}
            <p>
              When you&apos;re ready, start a session. Recording runs in the cloud
              while you&apos;re in the live session; when you&apos;re finished, end the
              session — we save the file to your library and run transcription in
              the background.
            </p>
            {hint ? (
              <pre
                className="error"
                style={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  fontSize: "0.9rem",
                  margin: "0.75rem 0",
                }}
              >
                {hint}
              </pre>
            ) : null}
            <div className="record-idle-actions">
              <button type="button" className="btn btn-primary" onClick={start} disabled={loading}>
                {loading ? "Starting…" : "Start log session"}
              </button>
            </div>
          </div>

          <section className="card record-faq" aria-labelledby="record-faq-heading">
            <h2 id="record-faq-heading" className="record-faq__heading">
              Recording tips
            </h2>
            <div className="record-faq__items">
              <details className="record-faq__item">
                <summary>What kind of session is this?</summary>
                <p>
                  This is a <strong>live session</strong> meant for stream-of-thought logging.
                  Recording is continuous—there&apos;s no pause button—but you get about{" "}
                  <strong>10 seconds</strong> after joining to get ready before capture starts.
                </p>
              </details>
              <details className="record-faq__item">
                <summary>Does recording length matter?</summary>
                <p>
                  Logs <strong>under about 5 minutes</strong> usually process faster. Longer
                  sessions are fine, but transcription and follow-up steps can take more time.
                </p>
              </details>
              <details className="record-faq__item">
                <summary>Can I share my recordings?</summary>
                <p>
                  <strong>No.</strong> Videos stay private to your account; there&apos;s no sharing
                  flow—they&apos;re for your personal library only.
                </p>
              </details>
              <details className="record-faq__item">
                <summary>What if I delete my account?</summary>
                <p>
                  Account deletion <strong>purges your data</strong>: recordings, transcriptions,
                  and related information tied to your account are removed.
                </p>
              </details>
              <details className="record-faq__item">
                <summary>How do dates and times work?</summary>
                <p>
                  Timestamps <strong>adjust to your timezone</strong> where we can detect it. You
                  can <strong>manually override</strong> them when you need to.
                </p>
              </details>
              <details className="record-faq__item">
                <summary>Is my data used to train models? How private is search?</summary>
                <p>
                  <strong>No models are trained on your data.</strong> Search only queries{" "}
                  <strong>your private neural index</strong>—not the public web or other
                  users&apos; libraries.
                </p>
              </details>
            </div>
          </section>
        </>
      ) : (
        <div className="record-session-overlay">
          <div
            ref={sessionPopoverRef}
            className="record-session-popover"
            role="dialog"
            aria-modal="true"
            aria-labelledby="record-session-dialog-title"
            tabIndex={-1}
          >
            <h2 id="record-session-dialog-title" className="record-session-popover__title">
              Log session
            </h2>
            {endBanner?.tone === "error" ? (
              <p className="error record-session-popover__banner">{endBanner.text}</p>
            ) : null}
            <div className="record-session-popover__body">
              <RealtimeSession
                authToken={session.authToken}
                onRegisterRelease={registerRelease}
              />
            </div>
            <footer className="record-session-popover__footer">
              <div className="record-session-popover__footer-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void endSession()}
                  disabled={sessionBusy}
                >
                  {ending ? "Saving recording…" : "End session & save"}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => void cancelSession()}
                  disabled={sessionBusy}
                >
                  {canceling ? "Discarding…" : "Discard session"}
                </button>
              </div>
              <p className="muted record-session-popover__footer-hint">
                Recording will start after 10s. Use mic/camera and settings in the panel above to
                adjust or turn off.
                <strong> End session &amp; save</strong> imports your log and runs transcription.{" "}
                <strong>Discard session</strong> ends the call and removes the draft without saving.
              </p>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
